<script setup>
import { computed, ref } from 'vue';
import { uploadFormFile } from '../api/forms';
import SchedulingPicker from './SchedulingPicker.vue';

const props = defineProps({
  field: { type: Object, required: true },
  modelValue: {
    type: [String, Number, Array, Boolean, Object, null],
    default: '',
  },
  error: { type: String, default: '' },
  // 'card': boxed field with its own label (single-page layout, unchanged).
  // 'bare': borderless/underline field with no internal label — used by the
  // steps (Typeform-style) layout, where the question title is rendered by
  // the parent above the field instead.
  variant: { type: String, default: 'card' },
});

const emit = defineEmits(['update:modelValue']);
const uploading = ref(false);
const uploadError = ref('');

const inputId = computed(() => `csf-field-${props.field.key}`);

const nativeInputType = computed(() => {
  const map = { phone: 'tel', cpf: 'text', cnpj: 'text', cep: 'text' };
  return map[props.field.type] || props.field.type;
});

// Campos brasileiros: máscara enquanto digita e, no CEP, o endereço aparece
// embaixo — é o que o cliente espera de um formulário feito aqui.
const MASKED = { cpf: 14, cnpj: 18, cep: 9 };
const isMasked = computed(() => Boolean(MASKED[props.field.type]));
const isRating = computed(() => props.field.type === 'rating');
// Agendamento: o valor não é texto, é o horário escolhido na agenda do
// campo — quem marca de verdade é o servidor, no envio.
const isScheduling = computed(() => props.field.type === 'scheduling');
const ratingMax = computed(() => Number(props.field.max) || 5);
const cepAddress = ref('');

const maskDocument = (type, raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  if (type === 'cpf')
    return d
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  if (type === 'cnpj')
    return d
      .slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/(\d{4})(\d)/, '$1-$2');
  return d.slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
};

const onMaskedInput = event => {
  const valor = maskDocument(props.field.type, event.target.value);
  event.target.value = valor;
  emit('update:modelValue', valor);
};

// Busca best-effort: se o serviço estiver fora, o campo continua valendo.
const lookupCep = async () => {
  if (props.field.type !== 'cep') return;
  const digits = String(props.modelValue || '').replace(/\D/g, '');
  if (digits.length !== 8) {
    cepAddress.value = '';
    return;
  }
  try {
    const resposta = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    const dados = await resposta.json();
    cepAddress.value = dados?.erro
      ? ''
      : [dados.logradouro, dados.bairro, `${dados.localidade}/${dados.uf}`]
          .filter(Boolean)
          .join(', ');
  } catch {
    cepAddress.value = '';
  }
};

// Options can arrive as plain strings or as { value, label } objects — support both
// so form authors aren't forced into one shape.
const normalizedOptions = computed(() =>
  (props.field.options || []).map(option => {
    if (option && typeof option === 'object') {
      return {
        value: option.value ?? option.label ?? '',
        label: option.label ?? option.value ?? '',
      };
    }
    return { value: option, label: option };
  })
);

const isChecked = value =>
  Array.isArray(props.modelValue) && props.modelValue.includes(value);

const toggleCheckboxOption = value => {
  const current = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
  const index = current.indexOf(value);
  if (index === -1) {
    current.push(value);
  } else {
    current.splice(index, 1);
  }
  emit('update:modelValue', current);
};

const onInput = event => emit('update:modelValue', event.target.value);
const onConsentToggle = event =>
  emit('update:modelValue', event.target.checked);
const onFile = async event => {
  const [file] = event.target.files || [];
  if (!file) return;
  uploadError.value = '';
  uploading.value = true;
  try {
    const uploaded = await uploadFormFile({
      slug: window.formSlug,
      file,
    });
    emit('update:modelValue', uploaded);
  } catch (uploadFailure) {
    uploadError.value =
      uploadFailure.response?.data?.error ||
      'Não foi possível enviar o arquivo.';
  } finally {
    uploading.value = false;
    event.target.value = '';
  }
};

const isBare = computed(() => props.variant === 'bare');

// Campos de escolha (select/radio/checkbox) e como exibi-los. O criador escolhe
// entre 'button' (botões clicáveis) e 'list' (dropdown p/ única, lista de
// checkboxes p/ múltipla) via field.choice_style. Default: radio/checkbox como
// botão, select como lista — mas o choice_style sempre manda.
const isChoiceField = computed(() =>
  ['select', 'radio', 'checkbox'].includes(props.field.type)
);
const isMultiChoice = computed(() => props.field.type === 'checkbox');
// Padrão = botões (visual limpo tipo Typeform); 'list' é opt-in explícito.
const asButtons = computed(() => props.field.choice_style !== 'list');
</script>

<template>
  <div
    class="csf-field"
    :class="{ 'csf-field--error': !!error, 'csf-field--bare': isBare }"
  >
    <template v-if="field.type === 'consent'">
      <label class="csf-consent" :for="inputId">
        <input
          :id="inputId"
          type="checkbox"
          class="csf-consent__input"
          :checked="modelValue === true"
          @change="onConsentToggle"
        />
        <span class="csf-consent__text" :data-required="field.required">{{
          field.label
        }}</span>
      </label>
    </template>

    <template v-else>
      <label
        v-if="!isBare"
        class="csf-label"
        :for="inputId"
        :data-required="field.required"
      >
        {{ field.label }}
      </label>

      <textarea
        v-if="field.type === 'textarea'"
        :id="inputId"
        class="csf-input csf-textarea"
        rows="4"
        :placeholder="field.placeholder"
        :required="field.required"
        :value="modelValue"
        @input="onInput"
      />

      <div v-else-if="field.type === 'file'" class="csf-list">
        <input
          :id="inputId"
          type="file"
          :required="field.required"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.zip"
          :disabled="uploading"
          @change="onFile"
        />
        <span v-if="uploading">Enviando arquivo…</span>
        <span v-else-if="modelValue?.filename">{{ modelValue.filename }}</span>
        <span v-if="uploadError" class="csf-error">{{ uploadError }}</span>
      </div>

      <!-- Campo de escolha: 'button' (botões) ou 'list' (dropdown p/ única,
           lista de checkboxes p/ múltipla) — ver asButtons/isMultiChoice. -->
      <template v-else-if="isChoiceField">
        <select
          v-if="!asButtons && !isMultiChoice"
          :id="inputId"
          class="csf-input csf-select"
          :required="field.required"
          :value="modelValue"
          @change="onInput"
        >
          <option value="" disabled>
            {{ field.placeholder || 'Selecione uma opção' }}
          </option>
          <option
            v-for="option in normalizedOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>

        <div v-else-if="!asButtons" class="csf-list">
          <label
            v-for="option in normalizedOptions"
            :key="option.value"
            class="csf-list__item"
          >
            <input
              type="checkbox"
              :checked="isChecked(option.value)"
              @change="toggleCheckboxOption(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>

        <div v-else-if="isMultiChoice" class="csf-choices">
          <label
            v-for="option in normalizedOptions"
            :key="option.value"
            class="csf-choice"
          >
            <input
              type="checkbox"
              :checked="isChecked(option.value)"
              @change="toggleCheckboxOption(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>

        <div v-else class="csf-choices" role="radiogroup">
          <label
            v-for="option in normalizedOptions"
            :key="option.value"
            class="csf-choice"
          >
            <input
              type="radio"
              :name="inputId"
              :value="option.value"
              :checked="modelValue === option.value"
              @change="emit('update:modelValue', option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>
      </template>

      <SchedulingPicker
        v-else-if="isScheduling"
        :field="field"
        :model-value="modelValue"
        @update:model-value="value => emit('update:modelValue', value)"
      />

      <div v-else-if="isRating" class="csf-rating">
        <button
          v-for="estrela in ratingMax"
          :key="estrela"
          type="button"
          class="csf-star"
          :class="{ 'csf-star--on': Number(modelValue) >= estrela }"
          :aria-label="`${estrela}`"
          @click="emit('update:modelValue', estrela)"
        >
          ★
        </button>
      </div>
      <template v-else-if="isMasked">
        <input
          :id="inputId"
          class="csf-input"
          type="text"
          inputmode="numeric"
          :placeholder="field.placeholder"
          :required="field.required"
          :value="modelValue"
          @input="onMaskedInput"
          @blur="lookupCep"
        />
        <p v-if="cepAddress" class="csf-hint">{{ cepAddress }}</p>
      </template>
      <input
        v-else
        :id="inputId"
        class="csf-input"
        :type="nativeInputType"
        :placeholder="field.placeholder"
        :required="field.required"
        :value="modelValue"
        @input="onInput"
      />
    </template>

    <p v-if="error" class="csf-error">{{ error }}</p>
  </div>
</template>

<style scoped>
.csf-rating {
  display: flex;
  gap: 6px;
}

.csf-star {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 30px;
  line-height: 1;
  padding: 0 2px;
  color: #cbd5e1;
  transition: color 0.12s ease;
}

.csf-star--on {
  color: #f59e0b;
}

.csf-hint {
  margin: 6px 0 0;
  font-size: 12px;
  opacity: 0.75;
}

.csf-field {
  margin-bottom: 20px;
}

.csf-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--csf-text);
}

.csf-label[data-required='true']::after,
.csf-consent__text[data-required='true']::after {
  content: ' *';
  color: #b3261e;
}

.csf-input {
  width: 100%;
  box-sizing: border-box;
  min-height: 46px;
  padding: 11px 14px;
  font-size: 15px;
  font-family: var(--csf-font);
  color: var(--csf-text);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: var(--csf-radius);
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.csf-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.csf-input:focus-visible,
.csf-consent__input:focus-visible,
.csf-choice:has(input:focus-visible) {
  border-color: var(--csf-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--csf-primary) 22%, transparent);
}

.csf-textarea {
  min-height: 96px;
  resize: vertical;
}

.csf-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9'%3E%3Cpath fill='none' stroke='%23555' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
}

.csf-field--error .csf-input {
  border-color: #b3261e;
}

.csf-error {
  margin: 6px 0 0;
  font-size: 12.5px;
  color: #b3261e;
}

.csf-choices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Opção de escolha (radio/checkbox/select) renderizada como BOTÃO clicável:
   o campo inteiro é a área de clique e o selecionado é destacado na cor
   primária. O input nativo fica escondido (mas acessível via o <label>). */
.csf-choice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  font-size: 15px;
  color: var(--csf-choice-text, var(--csf-text));
  background: var(--csf-card-bg, #fff);
  border: 1.5px solid rgba(0, 0, 0, 0.14);
  border-radius: calc(var(--csf-radius) * 0.7);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.csf-choice:hover {
  border-color: color-mix(in srgb, var(--csf-primary) 55%, rgba(0, 0, 0, 0.14));
}

.csf-choice:has(input:checked) {
  border-color: var(--csf-primary);
  background: color-mix(
    in srgb,
    var(--csf-primary) 10%,
    var(--csf-card-bg, #fff)
  );
  box-shadow: inset 0 0 0 1px var(--csf-primary);
  font-weight: 600;
}

.csf-choice input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

/* Estilo 'lista': dropdown p/ escolha única (csf-select) e, p/ múltipla, esta
   lista vertical limpa de checkboxes (substitui a antiga lista "feia"). */
.csf-list {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--csf-card-bg, #fff);
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: calc(var(--csf-radius) * 0.7);
}

.csf-list__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  font-size: 15px;
  color: var(--csf-text);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.csf-list__item + .csf-list__item {
  border-top: 1px solid color-mix(in srgb, var(--csf-text) 8%, transparent);
}

.csf-list__item:hover {
  background: color-mix(in srgb, var(--csf-primary) 6%, transparent);
}

.csf-list__item input {
  width: 17px;
  height: 17px;
  accent-color: var(--csf-primary);
}

.csf-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: calc(var(--csf-radius) * 0.7);
  cursor: pointer;
}

.csf-consent__input {
  margin-top: 2px;
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  accent-color: var(--csf-primary);
}

.csf-consent__text {
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--csf-text);
}

/* Bare variant — steps (Typeform-style) layout: no boxes, no fills, just an
 * underline. Every rule below is scoped under .csf-field--bare so the
 * single-page "card" experience above is completely untouched. Borders use
 * color-mix() against --csf-text/--csf-primary instead of hardcoded black,
 * since the page background is an arbitrary theme color, not always white. */
.csf-field--bare {
  margin-bottom: 0;
}

.csf-field--bare .csf-input {
  min-height: 0;
  padding: 12px 2px 14px;
  font-size: clamp(19px, 4.6vw, 25px);
  font-weight: 500;
  background: transparent;
  border: none;
  border-bottom: 2px solid color-mix(in srgb, var(--csf-text) 22%, transparent);
  border-radius: 0;
}

.csf-field--bare .csf-input::placeholder {
  color: color-mix(in srgb, var(--csf-text) 32%, transparent);
}

.csf-field--bare .csf-input:focus-visible {
  border-bottom-color: var(--csf-primary);
  box-shadow: none;
}

.csf-field--bare .csf-textarea {
  min-height: 84px;
}

.csf-field--bare .csf-select {
  padding-right: 30px;
  background-position: right 2px center;
}

/* No passo a passo (bare) as opções também são botões (mesmo estilo do card),
   só com um respiro maior — melhor pra toque no mobile. */
.csf-field--bare .csf-choices {
  gap: 10px;
}

.csf-field--bare .csf-choice {
  padding: 16px 18px;
}

.csf-field--bare .csf-consent {
  padding: 12px 2px;
  background: transparent;
  border: none;
  border-bottom: 1px solid color-mix(in srgb, var(--csf-text) 14%, transparent);
  border-radius: 0;
}

.csf-field--bare .csf-error {
  font-size: 13px;
}
</style>
